import { Fragment, useEffect, useMemo } from 'react';
import { Add, Clear } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, Stack, Typography } from '@mui/material';
import { Control, SetFieldValue, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { FormInput, FormInputProps } from '../../main-app/components/form/FormInput';
import { FormNumericInput } from '../../main-app/components/form/FormNumericInput';

type Props = {
  name?: string;
  readonly?: boolean;
  inputProps?: Partial<FormInputProps>;
};

export const ConnectedInvoiceItemsFormPart = ({ name = 'items', readonly, inputProps }: Props) => {
  const formContext = useFormContext();
  if (!formContext) {
    throw new Error('ConnectedInvoiceItemsFormPart: formContext is null');
  }

  return (
    <InvoiceItemsFormPart
      name={name}
      control={formContext.control}
      setValue={formContext.setValue}
      readonly={readonly}
      inputProps={inputProps}
    />
  );
};

const AutoFilledPriceSumInput = ({
  control,
  setValue,
  name,
  baseName,
  readonly,
  inputProps,
}: {
  control: Control;
  setValue: SetFieldValue<any>;
  name: string;
  baseName: string;
  readonly?: boolean;
  inputProps?: Partial<FormInputProps>;
}) => {
  const results = useWatch({
    control: control,
    name: baseName,
    defaultValue: '',
  });

  const total = useMemo(() => {
    if (!results.quantity || !results.pricePerUnit) {
      return null;
    }
    const quantity = parseFloat(results.quantity);
    const pricePerUnit = parseFloat(results.pricePerUnit);
    if (isNaN(quantity) || isNaN(pricePerUnit)) {
      return null;
    }

    let priceSum = quantity * pricePerUnit;

    if (results.vatRate) {
      const vatRate = parseFloat(results.vatRate);
      if (isNaN(vatRate)) {
        return null;
      }
      priceSum = priceSum * (1 + vatRate / 100);
    }

    return priceSum.toFixed(2);
  }, [results.pricePerUnit, results.quantity, results.vatRate]);

  useEffect(() => {
    if (!total) {
      return;
    }
    setValue(name, total);
  }, [name, setValue, total]);

  return (
    <FormNumericInput
      label={'Celkem *'}
      control={control}
      name={name}
      sx={{ width: 80 }}
      readonly={readonly}
      {...inputProps}
    />
  );
};

// New component for auto-calculating priceWithoutVat
const AutoFilledPriceWithoutVatInput = ({
  control,
  setValue,
  name,
  quantityName,
  pricePerUnitName,
  readonly,
  disabled,
  inputProps,
}: {
  control: Control;
  setValue: SetFieldValue<any>;
  name: string;
  quantityName: string;
  pricePerUnitName: string;
  readonly?: boolean;
  disabled?: boolean;
  inputProps?: Partial<FormInputProps>;
}) => {
  const pricePerUnit = useWatch({
    control: control,
    name: pricePerUnitName,
    defaultValue: '',
  });

  const quantity = useWatch({
    control: control,
    name: quantityName,
    defaultValue: '',
  });

  const priceWithoutVat = useMemo(() => {
    if (!pricePerUnit || !quantity || disabled) {
      return '';
    }
    const pricePerUnitNum = parseFloat(pricePerUnit);
    const quantityNum = parseFloat(quantity);
    if (isNaN(pricePerUnitNum) || isNaN(quantityNum)) {
      return '';
    }
    return (pricePerUnitNum * quantityNum).toFixed(2);
  }, [pricePerUnit, quantity, disabled]);

  useEffect(() => {
    if (priceWithoutVat) {
      setValue(name, priceWithoutVat);
    }
  }, [name, setValue, priceWithoutVat]);

  return (
    <FormNumericInput
      label={'Celkem bez DPH' + (disabled ? '' : ' *')}
      control={control}
      name={name}
      sx={{ width: 120 }}
      readonly={readonly}
      disabled={disabled}
      {...inputProps}
    />
  );
};

type InvoiceItemsFormPartProps = {
  control: Control<any>;
  setValue: SetFieldValue<any>;
  name?: string;
  readonly?: boolean;
  inputProps?: Partial<FormInputProps>;
  allowTaxPayer?: boolean;
};

export const InvoiceItemsFormPart = ({
  control,
  setValue,
  name = 'items',
  readonly,
  inputProps,
  allowTaxPayer = true,
}: InvoiceItemsFormPartProps) => {
  const { fields, append, remove } = useFieldArray({
    control: control,
    name: name,
  });

  // Watch the taxPayer value to conditionally enable/disable vatRate and priceWithoutVat
  const taxPayer = useWatch({
    control,
    name: 'taxPayer',
  });

  //clear taxPayer related fields if taxPayer is false
  useEffect(() => {
    if (!readonly && allowTaxPayer && taxPayer === false) {
      fields.forEach((_, index) => {
        setValue(`${name}.${index}.vatRate`, null);
        setValue(`${name}.${index}.priceWithoutVat`, null);
      });
    }
  }, [taxPayer, fields, setValue, readonly, allowTaxPayer]);

  const getLabelProps = (index: number) => {
    return {
      shrink: true,
      sx: {
        display: index > 0 ? { lg: 'none' } : undefined,
      },
    };
  };

  return (
    <Stack gap={1}>
      {!fields.length ? <Typography color={'text.secondary'}>žádné položky</Typography> : null}
      {fields.map((field, index) => {
        return (
          <Fragment key={field.id}>
            <Stack direction={'row'} flexWrap={'wrap'} gap={1}>
              <FormInput
                InputLabelProps={getLabelProps(index)}
                label={'Označení dodávky *'}
                aria-label={'Označení dodávky'}
                placeholder={''}
                control={control}
                name={`${name}.${index}.name`}
                sx={{ flex: 1, minWidth: 100 }}
                readonly={readonly}
                multiline={true}
                {...inputProps}
              />
              <FormNumericInput
                InputLabelProps={getLabelProps(index)}
                label={'Množství *'}
                aria-label={'Množství'}
                placeholder={'1'}
                control={control}
                name={`${name}.${index}.quantity`}
                sx={{ width: 60 }}
                readonly={readonly}
                {...inputProps}
              />
              <FormInput
                InputLabelProps={getLabelProps(index)}
                label={'m.j.'}
                aria-label={'m.j.'}
                placeholder={'ks'}
                control={control}
                name={`${name}.${index}.unit`}
                sx={{ width: 30 }}
                readonly={readonly}
                {...inputProps}
              />
              <FormNumericInput
                InputLabelProps={getLabelProps(index)}
                label={'Cena za m.j. *'}
                aria-label={'Cena za m.j.'}
                placeholder={'100'}
                control={control}
                name={`${name}.${index}.pricePerUnit`}
                sx={{ width: 120 }}
                readonly={readonly}
                {...inputProps}
              />
              {(readonly && !taxPayer) || !allowTaxPayer ? null : (
                <AutoFilledPriceWithoutVatInput
                  name={`${name}.${index}.priceWithoutVat`}
                  quantityName={`${name}.${index}.quantity`}
                  pricePerUnitName={`${name}.${index}.pricePerUnit`}
                  control={control}
                  setValue={setValue}
                  readonly={true}
                  disabled={!taxPayer}
                  inputProps={{
                    ...inputProps,
                    InputLabelProps: getLabelProps(index),
                  }}
                />
              )}
              {(readonly && !taxPayer) || !allowTaxPayer ? null : (
                <FormNumericInput
                  InputLabelProps={getLabelProps(index)}
                  label={'DPH (%)' + (taxPayer ? ' *' : '')}
                  aria-label={'DPH (%)'}
                  placeholder={'21'}
                  control={control}
                  name={`${name}.${index}.vatRate`}
                  sx={{ width: 75 }}
                  readonly={readonly}
                  disabled={!taxPayer}
                  {...inputProps}
                />
              )}
              <AutoFilledPriceSumInput
                name={`${name}.${index}.priceSum`}
                baseName={`${name}.${index}`}
                control={control}
                setValue={setValue}
                readonly={true}
                inputProps={{
                  ...inputProps,
                  InputProps: {
                    ...inputProps?.InputProps,
                  },
                  InputLabelProps: getLabelProps(index),
                }}
              />
              {readonly ? null : (
                <IconButton size={'small'} color={'error'} onClick={() => remove(index)} disabled={fields.length === 1}>
                  <Clear />
                </IconButton>
              )}
            </Stack>
          </Fragment>
        );
      })}
      <Box display={readonly ? 'none' : 'flex'} justifyContent={'flex-end'}>
        <Button
          variant={'outlined'}
          onClick={() =>
            append({
              name: '',
              quantity: '',
              unit: '',
              pricePerUnit: '',
              vatRate: '',
              priceWithoutVat: '',
              priceSum: '',
            })
          }
          startIcon={<Add />}
        >
          Přidat položku
        </Button>
      </Box>
    </Stack>
  );
};
